import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { CrmBoardService } from '../../services/crm-board.service';
import { BoardSummaryResponse } from '../../services/responses/board.response';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    edit?: BoardSummaryResponse | null;
    onClose: () => void;
    onCreated: () => void;
    refetchBoards: () => Promise<void>;
    setSelectedBoardId: (id: string) => void;
}

export const CrmBoardModal = ({ open, edit, onClose, onCreated, refetchBoards, setSelectedBoardId }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const boardService = new CrmBoardService();

    const schema = z.object({
        name: z.string().min(1, t('validacao.obrigatorio')).max(120),
        isDefault: z.boolean(),
        isIntake: z.boolean(),
    });

    type BoardForm = z.infer<typeof schema>;
    const form = useForm<BoardForm>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', isDefault: false, isIntake: false },
    });

    const { control, handleSubmit, reset, formState: { errors } } = form;

    useEffect(() => {
        if (open) {
            reset({
                name: edit?.name ?? '',
                isDefault: edit?.isDefault ?? false,
                isIntake: edit?.isIntake ?? false,
            });
        }
    }, [open, edit]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const saveChanges = async (data: BoardForm) => {
        setLoading(dispatch, true);
        try {
            if (edit) {
                await boardService.update(edit.id, {
                    name: data.name.trim(),
                    active: edit.active,
                    isDefault: data.isDefault,
                    isIntake: data.isIntake,
                });
                await refetchBoards();
                showAlert(t('general.atualizacao'), 'success', 2000);
                onCreated();
            } else {
                const res = await boardService.create({ name: data.name.trim() });
                await refetchBoards();
                setSelectedBoardId(res.data.data.id);
                showAlert(t('general.cadastro'), 'success', 2000);
                onCreated();
            }
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('crmKanban.criarQuadro')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(saveChanges)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('general.nome')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input type="text" autoComplete="off" size="medium" name="name" autoFocus />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>
                            {/* "Padrão" abre no CRM; "entrada" é onde o lead novo nasce: chaves separadas. */}
                            {edit && (
                                <Form.Field size={12}>
                                    <Controller
                                        name="isIntake"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.value}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                        color="info"
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2">{t('crmKanban.quadroEntrada')}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {t('crmKanban.quadroEntradaHelp')}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        )}
                                    />
                                </Form.Field>
                            )}

                            {edit && (
                                <Form.Field size={12}>
                                    <Controller
                                        name="isDefault"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.value}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                        color="success"
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2">{t('crmKanban.quadroPadrao')}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {t('crmKanban.quadroPadraoHelp')}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        )}
                                    />
                                </Form.Field>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box></Box>
                        <Box>
                            <MDButton sx={{ marginRight: '5px' }} variant="gradient" color="dark" onClick={onClose}>
                                {t('general.cancelar')}
                            </MDButton>
                            <MDButton variant="gradient" color="success" type="submit">
                                {t('general.salvar')}
                            </MDButton>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
